/**
 * Template Service
 *
 * Single source of truth for template discovery. Templates are AutoTemplate
 * `manifest.json` bundles published to Azure Front Door storage from the
 * Azure/ProjectAutoTemplates repo:
 *
 *   GET <endpoint>/<folder>/manifest.json         → JSON array of template ids
 *   GET <endpoint>/<folder>/<id>/manifest.json    → one AutoTemplate
 *
 * Requests are anonymous public GETs — plain `fetch`, deliberately not
 * `appFetch` (which attaches auth headers / local-mode routing).
 *
 * The `#workflowname#` placeholder rewriting is handled at apply time in
 * useTemplateApply, not here, so templates returned by this service may
 * still contain placeholders.
 */

import { z } from 'zod';
import type { TemplateDefinition } from '@/types/template';
import { validateAutoTemplate } from '@/types/autoTemplate';
import {
  autoTemplateToTemplateDefinition,
  extractTemplateExtras,
  type TemplateExtras,
} from '@/data/templates/templateAdapter';
import { getTemplatesEndpoint } from './endpoints';
import { telemetryService } from './telemetryService';

// Storage container folder holding the published template set. Both prod and
// canary endpoints publish /templates (verified 2026-08-28).
const STORAGE_TEMPLATES_FOLDER = 'templates';

/** How long a successful full fetch is served from the module cache. */
const CACHE_TTL_MS = 60 * 60 * 1000;

const templateIndexSchema = z.array(z.string().min(1));

// =============================================================================
// MODULE STATE
// =============================================================================

/**
 * Extras (trigger sample payload, per-action mocks) keyed by template id,
 * populated as templates are fetched. `buildTemplateMockSeed` reads this
 * synchronously inside `applyTemplate` — by then the gallery query has
 * already fetched the template, so the cache is warm by construction.
 */
const extrasCache = new Map<string, TemplateExtras>();

let cachedTemplates: { at: number; data: TemplateDefinition[] } | null = null;
let inFlight: Promise<TemplateDefinition[]> | null = null;

// =============================================================================
// FETCH HELPERS
// =============================================================================

async function fetchStorageJson(path: string): Promise<unknown> {
  const url = `${getTemplatesEndpoint()}/${STORAGE_TEMPLATES_FOLDER}/${path}`;
  const response = await fetch(url, { headers: { accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`Template storage request failed: ${response.status} ${url}`);
  }
  return response.json();
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Fetch the template index — the flat JSON array of template ids at
 * `<endpoint>/<folder>/manifest.json`.
 */
export async function fetchTemplateIndex(): Promise<string[]> {
  const raw = await fetchStorageJson('manifest.json');
  return templateIndexSchema.parse(raw);
}

/**
 * Fetch and validate a single template by id, returning both the adapted
 * `TemplateDefinition` and its extras (also cached for synchronous lookup
 * via {@link getFetchedTemplateExtras}).
 */
export async function fetchTemplateById(
  id: string,
): Promise<{ template: TemplateDefinition; extras: TemplateExtras }> {
  const raw = await fetchStorageJson(`${encodeURIComponent(id)}/manifest.json`);
  const validation = validateAutoTemplate(raw);
  if (!validation.success) {
    throw new Error(`Template "${id}" failed validation:\n${validation.errors.join('\n')}`);
  }
  const template = autoTemplateToTemplateDefinition(validation.data);
  const extras = extractTemplateExtras(validation.data);
  extrasCache.set(template.id, extras);
  return { template, extras };
}

/**
 * Fetch all available templates: index first, then each template in parallel.
 *
 * Fail-soft per template — a single broken/missing template is skipped (with
 * a console warning + telemetry) so the rest of the gallery still renders.
 * Fails hard only when the index fetch fails or every template fetch fails
 * (endpoint/CORS misconfiguration), so the gallery shows its error state
 * instead of a silently empty grid.
 */
export async function fetchAllTemplates(): Promise<TemplateDefinition[]> {
  const ids = await fetchTemplateIndex();
  if (ids.length === 0) return [];

  const results = await Promise.allSettled(ids.map((id) => fetchTemplateById(id)));

  const templates: TemplateDefinition[] = [];
  for (const [index, result] of results.entries()) {
    if (result.status === 'fulfilled') {
      templates.push(result.value.template);
    } else {
      const id = ids[index];
      console.warn(`[templateService] Skipping template "${id}":`, result.reason);
      telemetryService.trackEvent('templates.fetch_item_failed', { templateId: id });
    }
  }

  if (templates.length === 0) {
    throw new Error(
      `All ${ids.length} template fetches failed — check the templates endpoint configuration`,
    );
  }

  return templates;
}

/**
 * Deduped accessor over {@link fetchAllTemplates}: concurrent callers share
 * one in-flight request, and a successful result is served from the module
 * cache for {@link CACHE_TTL_MS}. Shared by the react-query queryFn and
 * `azureToolExecutor`; failures are not cached, so retries re-fetch.
 */
export function getAllTemplatesCached(): Promise<TemplateDefinition[]> {
  if (cachedTemplates && Date.now() - cachedTemplates.at < CACHE_TTL_MS) {
    return Promise.resolve(cachedTemplates.data);
  }
  if (!inFlight) {
    inFlight = fetchAllTemplates().then(
      (data) => {
        cachedTemplates = { at: Date.now(), data };
        inFlight = null;
        return data;
      },
      (error) => {
        inFlight = null;
        throw error;
      },
    );
  }
  return inFlight;
}

/**
 * Synchronous lookup of a fetched template's extras (trigger sample data,
 * mocks). Returns undefined when the template hasn't been fetched yet.
 */
export function getFetchedTemplateExtras(id: string): TemplateExtras | undefined {
  return extrasCache.get(id);
}

/** Test-only: clear all module-level caches. */
export function resetTemplateServiceForTests(): void {
  extrasCache.clear();
  cachedTemplates = null;
  inFlight = null;
}
